import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import ActionMenuItem from '../../components/ActionMenu/ActionMenuItem';
import DataTable from '../../components/DataTable/DataTable';
import { IColInfo } from '../../components/DataTable/Interfaces';
import SwitchField from '../../components/Fields/SwitchField';
import { FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from "../../components/Spinner/SpinnerOverlay";
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import BusinessPartnerGroupModel from '../../models/BusinessPartnerGroupModel';
import DataService from '../../services/data.service';
import Button from './../../components/Buttons/Button';
import ButtonBar from './../../components/Fields/ButtonBar';
import Form from './../../components/Fields/Form';
import SelectListField from './../../components/Fields/SelectListField';
import TextboxField from './../../components/Fields/TextboxField';
import Unauthorized from './../Authentication/Unauthorized';
import BusinessPartnerGroup_popup_CreateBusinessPartnerGroup from './Popups/BusinessPartnerGroup_popup_CreateBusinessPartnerGroup';

interface BusinessPartnerGroupSearchCriteria {
    quickSearch: string
    businessPartnerTypeId?: number
    rememberFilter: boolean,
}

const BusinessPartnerGroupSearchView: React.FC = () => {
    const businessPartnerTypes = useSelectListOptions('api/v1/selectlist/BusinessPartnerTypes?active=true&applicationFunction=' + ApplicationFunctions.ViewBusinessPartnerGroup, '-- All types');
    const [data, setData] = useState<BusinessPartnerGroupModel[]>([])
    const [showCreatePopup, setShowCreatePopup] = useState<boolean>(false);
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const { handleResult, handleException } = useNotifications();
    const { isAuthorizedFor } = useAuth();
    const navigate = useNavigate()
    const [formData, setFormData] = useState<BusinessPartnerGroupSearchCriteria>({
        quickSearch: '',
        rememberFilter: false,
    });

    useEffect(() => {
        const filterItem = localStorage.getItem("businessPartnerGroupFilter");
        if (filterItem !== null) {
            let filter: BusinessPartnerGroupSearchCriteria = JSON.parse(filterItem);
            setFormData(filter);
        }
        search()
    }, [])

    function search() {
        setShowSpinner(true);

        if (formData.rememberFilter)
            localStorage.setItem('businessPartnerGroupFilter', JSON.stringify(formData));
        else
            localStorage.removeItem(`businessPartnerGroupFilter`);

        DataService
            .post<BusinessPartnerGroupModel[]>(`api/v1/BusinessPartnerGroups/Search/`, formData)
            .then(response => {
                setShowSpinner(false);
                setData(response);
            })
            .catch(e => {
                setShowSpinner(false);
                handleException(e)
            });
    }

    function setDefaultCriteria() {
        let defaultCriteria = {
            quickSearch: '',
            rememberFilter: formData?.rememberFilter ?? false,
        }

        setFormData(defaultCriteria)
    }

    function deleteRow(rowData) {
        DataService.delete('api/v1/BusinessPartnerGroups/', rowData.id)
            .then(response => {
                handleResult(response);
                search();
            })
            .catch(e => { handleException(e); });
    }

    function onFieldChanged(name: string, value: string): void {
        formData[name] = value;
        setFormData(formData);

        console.log(name + ': ' + value);
    }

    function hideCreatePopup(id?: number) {
        setShowCreatePopup(false)
        if (id) {
            navigate('/plm/BusinessPartners/BusinessPartnerGroup/' + id)
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'quickSearch', label: 'Search', labelSize: 2 },
            { name: 'businessPartnerTypeId', label: 'Type', labelSize: 2 },
            { name: 'rememberFilter', label: 'Remember search criteria', labelSize: 2 },

        ],
        handleSubmit: search,
        handleFieldChanged: onFieldChanged,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData);

    const colInfos: IColInfo[] = [
        { key: true, dataProp: 'id', visible: false },
        { title: 'Code', dataProp: 'referenceCode', sort: 'asc', filterInfo: { filterType: 'Textbox' } },
        { title: 'Name', dataProp: 'name', sort: 'asc', filterInfo: { filterType: 'Textbox' } },
        { title: 'Type', dataProp: 'businessPartnerType.name', filterInfo: { filterType: 'CheckboxList' } },
        { title: 'Account manager', dataProp: 'accountManager.user', dataType: 'user', filterInfo: { filterType: 'Textbox' } },
    ];
    if (isAuthorizedFor(ApplicationFunctions.CreateBusinessPartnerGroup) || isAuthorizedFor(ApplicationFunctions.DeleteBusinessPartnerGroup)) {
        colInfos.push(
            {
                headerButtons: [{ text: "Add", icon: 'fa-plus-square', color: '#26A69A', onClick: () => { setShowCreatePopup(true) }, applicationFunctions: [ApplicationFunctions.CreateBusinessPartnerGroup] }],
                rowButtons: [{ text: "Delete", icon: 'fa-trash', color: '#F3565D', onClick: deleteRow, applicationFunctions: [ApplicationFunctions.DeleteBusinessPartnerGroup] }],
            });
    }

    if (!isAuthorizedFor(ApplicationFunctions.ViewBusinessPartnerGroup)) {
        return (<Unauthorized />);
    }

    const formActions =
        <ActionMenu title="Actions">
            <ActionMenuItem icon="fa fa-plus" iconVariant="success" title="Create new business-partner group" width={300} onClick={() => { setShowCreatePopup(true); }} appFunction={ApplicationFunctions.CreateBusinessPartnerGroup} />
        </ActionMenu>


    return (
        <div className='main-content'>
            <Form title='Business-partner groups' actions={formActions} footer={(<>
                <ButtonBar offset={2}>
                    <Button text='Search' {...getSubmitButtonProps()} />
                    <Button text='Clear' type='button' onClick={setDefaultCriteria} />
                </ButtonBar>
            </>)}>
                <Container fluid>
                    <TextboxField {...getFieldProps('quickSearch')} />
                    <SelectListField {...getFieldProps('businessPartnerTypeId')} options={businessPartnerTypes} />
                    <SwitchField {...getFieldProps('rememberFilter')} />
                </Container>
            </Form>
            <div className="card" style={{ margin: 12 }}>
                <DataTable
                    name='dtBusinessPartnerGroups'
                    colInfos={colInfos}
                    tableData={data}
                    navigateTitle='Business-partner group'
                    navigateUrl='/plm/BusinessPartners/BusinessPartnerGroup/'
                />
            </div>
            {showCreatePopup && <BusinessPartnerGroup_popup_CreateBusinessPartnerGroup show={showCreatePopup} onHide={hideCreatePopup} />}
            {showSpinner && (<SpinnerOverlay />)}
        </div>
    );
}
export default BusinessPartnerGroupSearchView;